import {Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Employee} from "../../../../../interfaces/Employee";
import {ActivatedRoute, Router} from "@angular/router";
import {EmployeeService} from "../../../../../services/employee.service";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Centre, CentreOption} from "../../../../../interfaces/Centres";
import {CentreService} from "../../../../../services/centre.service";
import {EducationObservationService} from "../education-observation.service";
import {EducationObservation} from "../education-observation.model";
import {LogService} from "../../../../../services/log.service";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {AddFieldComponent} from "../../../../../components/types/add-field/add-field.component";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {
  getCurrentUserLevel,
  levelFromRole,
  ObservationLevel,
  teacherTypeFromLevel
} from "../education-observation-access";

@Component({
  selector: 'app-add-edit-education-observation',
  templateUrl: './add-edit-education-observation.component.html',
  styleUrls: ['./add-edit-education-observation.component.scss']
})
export class AddEditEducationObservationComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  allCenters$!: Observable<CentreOption[]>;
  children: any[] = [];
  displayCentre = (c: any) => c?.centreName;
  displayChild = (c: any): string => {return c ? `${c.firstName} ${c.lastName}` : '';};
  sessionType: string[] = ['Activity Session','Baseline Assessment','Discharge Assessment','Group Session','Intermediate Assessment','Rapport Building',  'Regular Study' ];
  sessionDisplay = (value: string) => value;
  subjects: string[] = ['Language', 'Math', 'EVS & GK', 'Activity', 'NA'];
  subjectsDisplay = (value: string) => value;
  observerForm !: FormGroup;
  observerName: any[] = []
  allTeachers: any[]=[]
  employeeData!: Employee[];
  /** Active users eligible to appear in the Observer / Teacher pickers. */
  private selectableUsers: Employee[] = [];
  /** Level of the signed-in user — they may only observe someone below themselves. */
  private readonly currentUserLevel: ObservationLevel = getCurrentUserLevel();
  /** Level of the teacher currently chosen; the observer must sit above it. */
  private selectedTeacherLevel: ObservationLevel = ObservationLevel.NONE;
  currentEditId:any;
  isLoading: boolean = false;
  selectedCentre:any;
  selectedChild: any[] = [];
  @Input() valueKey: string = 'centreCode';

  constructor(private fb: FormBuilder,
              private educationObservationService : EducationObservationService,
              private router: Router,
              private route : ActivatedRoute,
              private centreService : CentreService,
              private userService: EmployeeService,
              @Inject(MAT_DIALOG_DATA) public id: string | null,
              private childService: CentreService,
              private matDialog : MatDialog,
              private mDatabase : AngularFireDatabase,
              private dialogRef: MatDialogRef<AddEditEducationObservationComponent>,
              private mLogService : LogService) {
    this.allCenters$ = this.centreService.getAllCentres();
    this.educationObservationTopics$ = this.mDatabase.list<string>('types/Education Observation Topics').valueChanges();
    this.educationObservationTopics$.pipe(takeUntil(this._destroyed$)).subscribe(res =>{
      console.log(res)
    });
    this.teachingAids$ = this.mDatabase.list<string>('types/Teaching Aid').valueChanges();
  }

  ngOnInit(): void {
    this.formInit();
    this.loadDirectory();
    this.currentEditId = this.id;

    if (this.currentEditId) {
      this.educationObservationService.getDataById(this.currentEditId).pipe(takeUntil(this._destroyed$)).subscribe((record: any) => {
        if (record) {
          this.observerForm.patchValue({
            sessionDate: record.sessionDate.toDate(),
            observerName: record.observerName,
            teacherName: record.teacherName,
            centreName: record.centreName,
            centreCode: record.centreCode,
            sessionType: record.sessionType,
            subject: record.subject,
            // topic: record?.topic,
            topic: Array.isArray(record?.topic) ? record.topic : (record?.topic ? [record.topic] : []),
            teachingAid: record?.teachingAid,
            observationComment: record?.observationComment,
            suggestions: record?.suggestions,
            children: record?.children || [],
            teacherType: record?.teacherType || '',
            observerType: record?.observerType || ''
          });

          this.syncTeacherDerivedState();

          // this.selectedEducationObservationTopic = record.topic;
          this.selectedEducationObservationTopic = Array.isArray(record?.topic) ? record.topic : (record?.topic ? [record.topic] : []);

          // this.selectedTeachingAid = record.teachingAid;
          this.selectedTeachingAid = Array.isArray(record?.teachingAid) ? record.teachingAid : (record?.teachingAid ? [record.teachingAid] : []);
          this.selectedCentre = { centreName: record.centreName, centreCode: record.centreCode };

          // Load children first, then set selectedChild
          this.applyCentreSelection({
            centreName: record.centreName,
            centreCode: record.centreCode
          }, record.children); // Pass children data
        }
      });
    }
  }
  private applyCentreSelection(centre: { centreName: string; centreCode: string }, savedChildren?: any[]) {
    if (!centre?.centreName || !centre?.centreCode) {
      return;
    }

    this.observerForm.patchValue({
      centreName: centre.centreName,
      centreCode: centre.centreCode,
      children: savedChildren || []  // Use saved children if available
    });

    this.observerForm.get('centreName')?.setErrors(null);
    this.observerForm.get('centreCode')?.setErrors(null);

    this.children = [];
    this.selectedChild = [];

    // Load children and then restore selection
    this.loadChildrenByCentre(centre.centreCode, savedChildren);
  }

  formInit() {
    this.observerForm = this.fb.group({
      sessionDate: ['', Validators.required],
      observerName: ['', Validators.required],
      teacherName: ['', Validators.required],
      centreName: ['', Validators.required],
      centreCode: ['', Validators.required],
      children: [[]],  // Changed to array instead of separate childName/childCode
      sessionType: ['', Validators.required],
      subject: ['', Validators.required],
      topic: [[]],
      teachingAid: [[]],
      observationComment: [''],
      suggestions: [''],
      // Both derived from the person selected beside them — not user-editable.
      // teacherType decides who can see the record. Kept enabled but rendered
      // readonly so the value shows in normal text; a disabled control greys it out.
      teacherType: [''],
      observerType: ['']
    });
  }
  submitForm() {
    if (this.observerForm.invalid) return;

    this.isLoading = true;
    const raw = this.observerForm.getRawValue();
    const payload: EducationObservation = {
      ...raw,
      sessionDate: new Date(raw.sessionDate)
    };

    if (this.currentEditId) {
      // 🔹 Update existing record
      this.educationObservationService.update(this.currentEditId, payload)
        .then(() => {
          const activity = {
            date: new Date().getTime(),
            section: 'Education Observation',
            action: 'Updated',
            description: `Education observation updated by user`,
            currentIp: localStorage.getItem('currentip')!
          };
          this.mLogService.addLog(activity);
          this.dialogRef.close();
          this.afterSave('Updated successfully!');
        })
        .catch((error) => {
          console.error('Error updating observation:', error);
          this.isLoading = false;
        });

    } else {
      // 🔹 Create new record
      this.educationObservationService.create(payload as EducationObservation)
        .then(() => {
          const activity = {
            date: new Date().getTime(),
            section: 'Education Observation',
            action: 'Created',
            description: `New education observation created by user`,
            currentIp: localStorage.getItem('currentip')!
          };
          this.mLogService.addLog(activity);
          this.dialogRef.close();
          this.afterSave('Saved successfully!');
        })
        .catch((error) => {
          console.error('Error creating observation:', error);
          this.isLoading = false;
        });
    }
  }


  private afterSave(message: string) {
    this.isLoading = false;
    alert(message);
    this.router.navigate(['/home/education/education-observation-list']);
    this.observerForm.reset();
    this.currentEditId = null;
  }

  /** Excluded from both pickers by request; kept in one place rather than duplicated per dropdown. */
  private static readonly EXCLUDED_NAMES = ['Shilpa Pahuja', 'Mukti Panchamia'];

  /**
   * Loads the user directory once and derives both pickers from it.
   * Replaces the two separate getUsers() subscriptions that each rebuilt employeeData.
   */
  private loadDirectory() {
    this.userService.getUsers().pipe(takeUntil(this._destroyed$)).subscribe(snapshotchanges => {
      this.employeeData = snapshotchanges.map(w => {
        let emp = w.payload.toJSON() as Employee;
        if (w.key != null) {
          emp['id'] = w.key;
        }
        return emp;
      });

      this.selectableUsers = this.employeeData.filter(employee =>
        employee?.disabled !== true &&
        employee?.jobtitle !== 'IT' &&
        !!employee?.userName &&
        !AddEditEducationObservationComponent.EXCLUDED_NAMES.includes(employee.userName));

      this.refreshTeacherOptions();
      // The record may have loaded before the directory did, so re-derive now.
      this.syncTeacherDerivedState();
    });
  }

  /**
   * Re-derives teacherType and the observer list from whichever teacher is
   * currently selected. Safe to call before the directory has loaded.
   */
  private syncTeacherDerivedState() {
    const teacherName = this.observerForm?.get('teacherName')?.value;
    const level = this.levelOfTeacherNamed(teacherName);

    this.selectedTeacherLevel = level;
    if (level !== ObservationLevel.NONE) {
      this.observerForm.patchValue({ teacherType: teacherTypeFromLevel(level) });
    }

    this.refreshObserverOptions();
  }

  private toOptions(employees: Employee[]) {
    return employees
      .map(employee => ({ name: employee.userName }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Teachers the signed-in user is allowed to observe: anyone inside the teaching
   * hierarchy who sits strictly below them.
   */
  private refreshTeacherOptions() {
    this.allTeachers = this.toOptions(this.selectableUsers.filter(employee => {
      const level = levelFromRole(employee?.role);
      return level >= ObservationLevel.TEACHING_TEAM &&
        level <= ObservationLevel.LEAD_TEACHER &&
        level < this.currentUserLevel;
    }));
  }

  /**
   * Observers valid for the chosen teacher. Observations are carried out by
   * Senior and Lead Teachers only — no Admin/Management or other staff — and the
   * observer must still sit above the teacher being observed.
   */
  private refreshObserverOptions() {
    const minimumLevel = this.selectedTeacherLevel !== ObservationLevel.NONE
      ? this.selectedTeacherLevel
      : ObservationLevel.TEACHING_TEAM;

    this.observerName = this.toOptions(this.selectableUsers.filter(employee => {
      const level = levelFromRole(employee?.role);
      return (level === ObservationLevel.SENIOR_TEACHER || level === ObservationLevel.LEAD_TEACHER) &&
        level > minimumLevel;
    }));

    // Drop an observer that the new teacher selection has invalidated.
    const currentObserver = this.observerForm?.get('observerName')?.value;
    if (currentObserver && !this.observerName.some(option => option.name === currentObserver)) {
      this.observerForm.patchValue({ observerName: '' });
    }

    this.syncObserverDerivedState();
  }

  /** Level of a teacher by name, from the live directory. */
  private levelOfTeacherNamed(name?: string | null): ObservationLevel {
    const teacher = this.employeeData?.find(employee => employee.userName === name);
    return levelFromRole(teacher?.role);
  }
  resetForm() {
    this.observerForm.reset();
    this.currentEditId = null;
  }
  onCancel() {
    this.dialogRef.close();
  }

  get topicLength(): number {
    const val: string = this.observerForm.get('topic')?.value || [];
    return val.length;
  }
  get teachingAidLength(): number {
    const val: string = this.observerForm.get('teachingAid')?.value || [];
    return val.length;
  }
  get suggestionsLength(): number {
    const val: string = this.observerForm.get('suggestions')?.value || '';
    return val.length;
  }
  get observationCommentLength(): number {
    const val: string = this.observerForm.get('observationComment')?.value || '';
    return val.length;
  }
  onObserverSelected(selected: any) {
    this.observerForm.get('observerName')?.setValue(selected.name);
    this.syncObserverDerivedState();
  }

  /**
   * Fills Observer Type from whoever is selected as observer. Observers are
   * Senior/Lead Teachers, but legacy records may name an Admin, so fall back to
   * the raw role rather than showing a blank.
   */
  private syncObserverDerivedState() {
    const observerName = this.observerForm?.get('observerName')?.value;
    const observer = this.employeeData?.find(employee => employee.userName === observerName);

    const derived = teacherTypeFromLevel(levelFromRole(observer?.role)) || observer?.role || '';
    this.observerForm.patchValue({ observerType: derived });
  }
  onTeacherSelected(selected: any) {
    this.observerForm.get('teacherName')?.setValue(selected.name);

    // Teaching Team / Senior Teacher / Lead Teacher — Senior Teacher used to be
    // collapsed into Lead Teacher, which over-restricted those records.
    this.syncTeacherDerivedState();
  }
  // onChildSelect(child: any) {
  //   if (!child) return;
  //   this.observerForm.patchValue({
  //     childName: `${child.firstName} ${child.lastName}`,
  //     childCode: child.childCode
  //   });
  //   this.selectedChild = child;
  //   // Clear validation errors (important)
  //   this.observerForm.get('childName')?.setErrors(null);
  //   this.observerForm.get('childCode')?.setErrors(null);f
  // }

  onChildSelect(selectedChildren: any) {
    if (!selectedChildren || selectedChildren.length === 0) {
      this.observerForm.patchValue({
        children: []
      });
      this.selectedChild = [];
      return;
    }

    // Map selected children to include both name and code
    const childrenArray = selectedChildren.map((child: any) => ({
      childName: `${child.firstName} ${child.lastName}`,
      childCode: child.childCode
    }));

    this.observerForm.patchValue({
      children: childrenArray
    });

    this.selectedChild = selectedChildren;

    // Clear validation errors
    this.observerForm.get('children')?.setErrors(null);
  }



  onCentreSelect($event: any) {
    console.log($event);

    if ($event && $event.centreName && $event.centreCode) {
      this.observerForm.patchValue({
        centreName: $event.centreName,
        centreCode: $event.centreCode,
        children: []  // Clear children array
      });

      this.observerForm.get('centreName')?.setErrors(null);
      this.observerForm.get('centreCode')?.setErrors(null);
      this.children = [];
      this.selectedChild = [];  // Clear selected child
      this.loadChildrenByCentre($event.centreCode);
    } else {
      this.observerForm.patchValue({
        centreName: '',
        centreCode: '',
        children: []  // Clear children array
      });

      this.observerForm.get('centreName')?.setErrors({ invalidSelection: true });
      this.observerForm.get('centreCode')?.setErrors({ invalidSelection: true });
    }
  }

  loadChildrenByCentre(centreCode: string, savedChildren?: any[]) {
    this.isLoading = true;
    this.childService.getChildrenByCentre(centreCode).pipe(takeUntil(this._destroyed$)).subscribe(data => {
      this.children = data.filter(child =>
        child.centreName !== 'Om Shanti' &&
        child.isAlloted === true
      );

      // After loading children, restore the selected children in edit mode
      if (savedChildren && savedChildren.length > 0) {
        this.selectedChild = this.children.filter(child =>
          savedChildren.some(saved => saved.childCode === child.childCode)
        );
      }

      this.isLoading = false;
    });
  }




  onSessionSelect($event: any) {
    this.observerForm.patchValue({
      sessionType: $event
    })
  }
  onSubjectsSelect($event: any) {
    this.observerForm.patchValue({
      subject: $event
    })
  }

  handleAdd(event: any) {
    console.log("Add button clicked in child:", event);
    this.openAddTypeDialog(); //
  }
  handleAddTeachingAid(event: any) {
    console.log("Add button clicked in child:", event);
    this.openAddTeachingAidDialog(); //
  }


  allTypes: any[] = [];
  allTeachingAid: any[] = [];
  filteredTypes: any[] = [];
  filteredTeachingAid: any[] = [];
  displayType = (c: any) => c;
  educationObservationTopics$!: Observable<any[]>
  teachingAids$!: Observable<any[]>
  selectedEducationObservationTopic: any [] = [];
  selectedTeachingAid:any [] =[];
  openAddTypeDialog(): void {
    const dialogRef = this.matDialog.open(AddFieldComponent, {
      width: '400px',
      disableClose: true,
      data: 'Education Observation Topics'
    });

    dialogRef.afterClosed().pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.mDatabase.list('types/Education Observation Topics').valueChanges().pipe(takeUntil(this._destroyed$)).subscribe(types => {
        this.allTypes = types;
        this.filteredTypes = [...types];
      });
    });
  }


  openAddTeachingAidDialog(): void {
    const dialogRef = this.matDialog.open(AddFieldComponent, {
      width: '400px',
      disableClose: true,
      data: 'Teaching Aid'
    });

    dialogRef.afterClosed().pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.mDatabase.list('types/Teaching Aid').valueChanges().pipe(takeUntil(this._destroyed$)).subscribe(types => {
        this.allTeachingAid = types;
        this.filteredTeachingAid = [...types];
      });
    });
  }

  // protected onSelectEducationObservationtopics(e: any) {
  //   this.observerForm.patchValue({
  //     topic: e,
  //   })
  // }
  onSelectEducationObservationtopics(event: any[]) {
    console.log(event)
    this.selectedEducationObservationTopic = event ?? [];
    this.observerForm.patchValue({
      topic: this.selectedEducationObservationTopic
    });
  }


  protected onSelectTeachingAids(e: any[]) {
    this.selectedTeachingAid = e ?? [];
    this.observerForm.patchValue({
      teachingAid: e,
    })
  }
bulkChangeObservationData(){
    this.educationObservationService.bulkChangeObservationData()
}

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

}
